/* ══════════════════════════════════════════════════════════════════════
   CLIENT DRAWER
   Right-side detail panel. Opens when clicking a client row anywhere.
   Shows pacing, run-rate, alerts, inbox for that one client.
   ══════════════════════════════════════════════════════════════════════ */

import { StatusChip, Mono } from './StatusChip';
import { fmt, fmtGap, sevVariant, sevColor, paceVariant, confVariant } from '../api/format';
import type { OverviewClient, Alert, PacingRow, RunRateClient, ShadowCount } from '../api/types';

interface ClientDrawerProps {
  client: OverviewClient | null;
  alerts: Alert[];
  pacing: PacingRow[];
  runRate: RunRateClient | null;
  shadowCount: ShadowCount | null;
  onClose: () => void;
}

export function ClientDrawer({ client, alerts, pacing, runRate, shadowCount, onClose }: ClientDrawerProps) {
  if (!client) return null;

  const openAlerts = alerts.filter(a => a.state === 'open');
  const inboxCount = shadowCount?.by_client?.[client.id] ?? 0;

  return (
    <>
      {/* Backdrop */}
      <div onClick={onClose} style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
        zIndex: 100, cursor: 'pointer',
      }} />

      {/* Drawer */}
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: 480,
        background: 'var(--bg-root)', borderLeft: '1px solid var(--border-subtle)',
        zIndex: 101, overflowY: 'auto', padding: 0,
        animation: 'fadeUp 0.2s ease',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '16px 20px', borderBottom: '1px solid var(--border-subtle)',
          background: 'var(--bg-raised)', position: 'sticky', top: 0, zIndex: 1,
        }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>{client.name}</div>
            <div style={{ fontSize: 11, color: 'var(--text-ghost)', marginTop: 2 }}>
              {client.is_active ? 'Active' : 'Inactive'} · {client.id}
            </div>
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', color: 'var(--text-ghost)',
            fontSize: 20, cursor: 'pointer', padding: '4px 8px',
          }}>×</button>
        </div>

        <div style={{ padding: 20 }}>
          {/* MTD Summary */}
          <Section title="MTD Metrics">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
              {[
                { l: 'Sent', v: fmt(client.mtd.sent) },
                { l: 'Replied', v: fmt(client.mtd.replied), c: client.mtd.replied > 0 ? 'var(--green-bright)' : undefined },
                { l: 'Reply Rate', v: client.reply_rate != null ? client.reply_rate.toFixed(1) + '%' : '—' },
                { l: 'PV+', v: fmt(client.mtd.pv_positive) },
                { l: 'Missive+', v: fmt(client.mtd.missive_positive), c: client.mtd.missive_positive > 0 ? 'var(--blue-bright)' : undefined },
                { l: 'Bounced', v: fmt(client.mtd.bounced), c: client.mtd.bounced > 0 ? 'var(--red)' : undefined },
              ].map(x => (
                <div key={x.l} style={{ padding: 8, background: 'var(--bg-surface)', borderRadius: 'var(--radius-sm)', textAlign: 'center' }}>
                  <div style={{ fontSize: 9, color: 'var(--text-ghost)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>{x.l}</div>
                  <Mono color={x.c || 'var(--text-primary)'} style={{ fontSize: 16, fontWeight: 700 }}>{x.v}</Mono>
                </div>
              ))}
            </div>
          </Section>

          {/* Alerts */}
          <Section title={`Alerts (${openAlerts.length} open)`}>
            {openAlerts.length === 0 ? (
              <div style={{ fontSize: 12, color: 'var(--text-ghost)', padding: 12, textAlign: 'center' }}>No open alerts</div>
            ) : openAlerts.map(a => (
              <div key={a.id} style={{
                padding: '10px 12px', borderLeft: `3px solid ${sevColor(a.severity)}`,
                background: 'var(--bg-surface)', borderRadius: '0 var(--radius-sm) var(--radius-sm) 0', marginBottom: 6,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>{a.title}</span>
                  <StatusChip label={a.severity} variant={sevVariant(a.severity)} />
                </div>
                <div style={{ fontSize: 10, color: 'var(--text-ghost)', marginTop: 2 }}>{a.detail}</div>
              </div>
            ))}
          </Section>

          {/* Pacing */}
          <Section title={`Pacing (${pacing.length} metrics)`}>
            {pacing.length === 0 ? (
              <div style={{ fontSize: 12, color: 'var(--text-ghost)', padding: 12, textAlign: 'center' }}>No pacing data</div>
            ) : pacing.map(p => (
              <div key={p.metric} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '8px 12px', background: 'var(--bg-surface)', borderRadius: 'var(--radius-sm)',
                marginBottom: 4, opacity: p.is_proxy ? 0.7 : 1,
              }}>
                <div>
                  <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{p.metric.replace(/_/g, ' ')}</span>
                  {p.is_proxy === 1 && <StatusChip label="proxy" variant="proxy" />}
                </div>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  <Mono color="var(--text-muted)" style={{ fontSize: 11 }}>{p.mtd}/{p.target}</Mono>
                  <StatusChip label={p.pace_status.replace(/_/g, ' ')} variant={paceVariant(p.pace_status)} />
                </div>
              </div>
            ))}
          </Section>

          {/* Run Rate */}
          <Section title="Run-Rate">
            {!runRate ? (
              <div style={{ fontSize: 12, color: 'var(--text-ghost)', padding: 12, textAlign: 'center' }}>No run-rate data</div>
            ) : (
              <div style={{ padding: 12, background: 'var(--bg-surface)', borderRadius: 'var(--radius-sm)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Gap: <Mono color={runRate.gap > 0 ? 'var(--red)' : 'var(--green)'} style={{ fontWeight: 700 }}>{fmtGap(runRate.gap)}</Mono></span>
                  <StatusChip label={`${runRate.confidence} confidence`} variant={confVariant(runRate.confidence)} />
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-ghost)' }}>
                  {runRate.required.missive_positive_per_day != null
                    ? `Need ${runRate.required.missive_positive_per_day.toFixed(1)} M+/day for remaining ${runRate.biz_days_remaining} biz days`
                    : runRate.conversion_rates.note || 'Conversion rates not available'
                  }
                </div>
              </div>
            )}
          </Section>

          {/* Inbox */}
          <Section title="Inbox">
            <div style={{ padding: 12, background: 'var(--bg-surface)', borderRadius: 'var(--radius-sm)', display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Interested conversations</span>
              <Mono color={inboxCount > 0 ? 'var(--blue-bright)' : 'var(--text-dim)'} style={{ fontWeight: 700 }}>{inboxCount}</Mono>
            </div>
          </Section>
        </div>
      </div>
    </>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <h3 style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-ghost)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>{title}</h3>
      {children}
    </div>
  );
}
